import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PROJECT_AUDIT_QUERY_KEY, PROJECT_QUERY_KEY, STUDIO_RECENT_KEY } from "../constants";
import {
  createStudioProject,
  deleteStudioProject,
  duplicateStudioProject,
  listStudioProjects,
  setProjectArchived,
  setProjectFavorite,
  updateStudioProject,
} from "../services/projectService";
import { fetchProjectAuditLogs, logProjectAudit } from "../services/projectAuditService";

function actorFromUser(user) {
  return {
    adminUid: user?.uid || "",
    adminName: user?.email || user?.username || "",
  };
}

function touchRecent(projectId) {
  try {
    const raw = localStorage.getItem(STUDIO_RECENT_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const next = [projectId, ...list.filter((id) => id !== projectId)].slice(0, 8);
    localStorage.setItem(STUDIO_RECENT_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

export function useRecentProjectIds() {
  try {
    const raw = localStorage.getItem(STUDIO_RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function useStudioProjects() {
  return useQuery({
    queryKey: PROJECT_QUERY_KEY,
    queryFn: listStudioProjects,
    staleTime: 30_000,
  });
}

export function useProjectAuditLogs() {
  return useQuery({
    queryKey: PROJECT_AUDIT_QUERY_KEY,
    queryFn: () => fetchProjectAuditLogs({ max: 50 }),
    staleTime: 15_000,
  });
}

export function useProjectMutations(user) {
  const queryClient = useQueryClient();
  const actor = actorFromUser(user);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: PROJECT_QUERY_KEY });
  const invalidateAudit = () => queryClient.invalidateQueries({ queryKey: PROJECT_AUDIT_QUERY_KEY });

  const optimistic = (updater) => {
    queryClient.setQueryData(PROJECT_QUERY_KEY, (old) => {
      if (!Array.isArray(old)) return old;
      return updater(old);
    });
  };

  const createMutation = useMutation({
    mutationFn: (payload) => createStudioProject(payload, actor.adminName),
    onSuccess: async (project) => {
      await logProjectAudit({
        action: "create",
        projectId: project.id,
        projectName: project.name,
        ...actor,
        details: { firebaseProjectId: project.firebaseProjectId },
      });
      await invalidate();
      await invalidateAudit();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateStudioProject(id, payload, actor.adminName),
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({ queryKey: PROJECT_QUERY_KEY });
      const prev = queryClient.getQueryData(PROJECT_QUERY_KEY);
      optimistic((list) =>
        list.map((p) =>
          p.id === id ? { ...p, ...payload, updatedAt: new Date().toISOString() } : p
        )
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(PROJECT_QUERY_KEY, ctx.prev);
    },
    onSuccess: async (project) => {
      await logProjectAudit({
        action: "update",
        projectId: project.id,
        projectName: project.name,
        ...actor,
      });
      await invalidate();
      await invalidateAudit();
    },
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }) => setProjectArchived(id, archived, actor.adminName),
    onMutate: async ({ id, archived }) => {
      await queryClient.cancelQueries({ queryKey: PROJECT_QUERY_KEY });
      const prev = queryClient.getQueryData(PROJECT_QUERY_KEY);
      optimistic((list) =>
        list.map((p) =>
          p.id === id ? { ...p, archived, updatedAt: new Date().toISOString() } : p
        )
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(PROJECT_QUERY_KEY, ctx.prev);
    },
    onSuccess: async (project, { archived }) => {
      await logProjectAudit({
        action: archived ? "archive" : "restore",
        projectId: project.id,
        projectName: project.name,
        ...actor,
      });
      await invalidate();
      await invalidateAudit();
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ id, favorite }) => setProjectFavorite(id, favorite, actor.adminName),
    onMutate: async ({ id, favorite }) => {
      await queryClient.cancelQueries({ queryKey: PROJECT_QUERY_KEY });
      const prev = queryClient.getQueryData(PROJECT_QUERY_KEY);
      optimistic((list) =>
        list.map((p) => (p.id === id ? { ...p, favorite } : p))
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(PROJECT_QUERY_KEY, ctx.prev);
    },
    onSuccess: async (project, { favorite }) => {
      await logProjectAudit({
        action: favorite ? "favorite" : "unfavorite",
        projectId: project.id,
        projectName: project.name,
        ...actor,
      });
      await invalidate();
      await invalidateAudit();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ project, reason }) => deleteStudioProject(project.id, { reason }),
    onMutate: async ({ project }) => {
      await queryClient.cancelQueries({ queryKey: PROJECT_QUERY_KEY });
      const prev = queryClient.getQueryData(PROJECT_QUERY_KEY);
      optimistic((list) => list.filter((p) => p.id !== project.id));
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(PROJECT_QUERY_KEY, ctx.prev);
    },
    onSuccess: async (_void, { project, reason }) => {
      await logProjectAudit({
        action: "delete",
        projectId: project.id,
        projectName: project.name,
        ...actor,
        details: { reason: reason || "", firebaseProjectId: project.firebaseProjectId },
      });
      await invalidate();
      await invalidateAudit();
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (sourceId) => duplicateStudioProject(sourceId, actor.adminName),
    onSuccess: async (project, sourceId) => {
      await logProjectAudit({
        action: "duplicate",
        projectId: project.id,
        projectName: project.name,
        ...actor,
        details: { sourceId },
      });
      await invalidate();
      await invalidateAudit();
    },
  });

  return {
    createMutation,
    updateMutation,
    archiveMutation,
    favoriteMutation,
    deleteMutation,
    duplicateMutation,
    touchRecent,
  };
}
